import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { HttpTicketInvoiceApprovalPaymentService } from '../../../service/http-ticket-invoice-approval.service';
import { TicketInvoiceDto, TicketInvoiceLineDto } from '../../../service/dto';

@Component({
	selector: 'app-ticket-invoice-list',
	standalone: false,
	templateUrl: './ticket-invoice-list.component.html',
	styleUrl: './ticket-invoice-list.component.scss'
})
export class TicketInvoiceListComponent implements OnInit, OnDestroy {

	// ── State ──────────────────────────────────────────────────────────
	invoices: TicketInvoiceDto[] = [];
	expandedRows = new Set<number>();

	isLoading = false;
	errorMessage: string | null = null;

	// ── Filter form ────────────────────────────────────────────────────
	filterForm!: FormGroup;

	private destroy$ = new Subject<void>();

	constructor(private fb: FormBuilder, private httpService: HttpTicketInvoiceApprovalPaymentService) { }

	// ── Lifecycle ──────────────────────────────────────────────────────
	ngOnInit(): void {
		this.filterForm = this.fb.group({
			fromDate: [this.defaultFromDate(),Validators.required],
			toDate: [new Date(),Validators.required],
		});
		// this.loadInvoices();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ── Data loading ───────────────────────────────────────────────────
	loadInvoices(): void {
		let fromDate = this.filterForm.get('fromDate')!.value;
		let toDate = this.filterForm.get('toDate')!.value;

		this.isLoading = true;
		this.errorMessage = null;

		this.httpService
			.getApprovedInvoices(fromDate, toDate)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => (this.isLoading = false))
			)
			.subscribe({
				next: (result) => {
					// getApprovedInvoices currently returns Observable<TicketInvoiceDto>.
					// If the backend returns a ResultListDto, adjust here: this.invoices = result.items
					this.invoices = Array.isArray(result) ? result : [result];
				},
				error: (err) => {
					console.error('Failed to load approved invoices', err);
					this.errorMessage = 'Failed to load invoices. Please try again.';
				},
			});
	}

	onApplyFilter(): void {
		this.expandedRows.clear();
		this.loadInvoices();
	}

	// ── Row expansion ──────────────────────────────────────────────────
	toggleRow(id: number): void {
		this.expandedRows.has(id)
			? this.expandedRows.delete(id)
			: this.expandedRows.add(id);
	}

	isExpanded(id: number): boolean {
		return this.expandedRows.has(id);
	}

	// ── Calculations ───────────────────────────────────────────────────
	invoiceTotal(invoice: TicketInvoiceDto): number {
		return invoice.lines?.reduce((sum, l) => sum + (l.cost * l.amount), 0) ?? 0;
	}

	lineTotal(line: TicketInvoiceLineDto): number {
		return line.cost * line.amount;
	}

	grandTotal(): number {
		return this.invoices.reduce((sum, inv) => sum + this.invoiceTotal(inv), 0);
	}

	get invoiceCount(): number {
		return this.invoices.length;
	}

	// ── Formatting ─────────────────────────────────────────────────────
	formatCurrency(value: number): string {
		return new Intl.NumberFormat('en-ZA', {
			style: 'currency',
			currency: 'ZAR',
			minimumFractionDigits: 2,
		}).format(value);
	}

	formatDate(date: Date | string | null | undefined): string {
		if (!date) return '—';
		return new Intl.DateTimeFormat('en-ZA', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		}).format(new Date(date));
	}

	recipientLabel(invoice: TicketInvoiceDto): string {
		if (invoice.recipientMember) {
			const m = invoice.recipientMember;
			// Adjust to your MemberDto fields
			return (m as any).fullName ?? (m as any).name ?? `Member #${(m as any).id}`;
		}
		if (invoice.recipientDynamicGenetic) {
			return (invoice.recipientDynamicGenetic as any).name ?? 'Unknown entity';
		}
		return '—';
	}

	// ── Helpers ────────────────────────────────────────────────────────
	private defaultFromDate(): Date {
		const d = new Date();
		d.setMonth(d.getMonth() - 1);
		return d;
	}

	getInvoiceCount(invoices: TicketInvoiceDto[]): number {
		return invoices.reduce((sum, inv) => sum + (inv.lines?.length ?? 0), 0);
	}
}
